/**
 * Shared helpers that turn raw userData field values into human-readable text.
 * Used by RenderDialog interpolation, review dialogs, and personal_diagram nodes.
 */

export type FieldOption = { key: string; label?: string };

/**
 * Look up the options array for a field key by searching all activity nodes'
 * custom blocks (checkbox options live on content.options).
 */
export function findFieldOptions(
  diagramData: Record<string, any> | null | undefined,
  fieldKey: string,
): FieldOption[] | null {
  if (!diagramData || !fieldKey) return null;
  for (const node of Object.values(diagramData)) {
    const custom = node?.custom;
    if (!Array.isArray(custom)) continue;
    for (const comp of custom) {
      if (comp.content?.key === fieldKey && Array.isArray(comp.content?.options)) {
        return comp.content.options;
      }
    }
  }
  return null;
}

function isEmptyValue(fieldValue: unknown): boolean {
  if (fieldValue === null || fieldValue === undefined) return true;
  if (typeof fieldValue === "string" && fieldValue.trim() === "") return true;
  return false;
}

/**
 * Format a raw userData value for display.
 * Returns "" for null/undefined/empty string so callers can show a placeholder.
 */
export function formatFieldValue(
  fieldValue: unknown,
  fieldOptions?: FieldOption[] | null,
): string {
  if (isEmptyValue(fieldValue)) {
    return "";
  }

  if (typeof fieldValue === "string") {
    // richtext_dynamic (and similar) may store HTML — show plain text in diagrams
    if (/<[a-z][\s\S]*>/i.test(fieldValue)) {
      const plain = fieldValue
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/\n{3,}/g, "\n\n")
        .trim();
      return plain;
    }
    return fieldValue;
  }

  if (typeof fieldValue === "number") {
    // slider, timestamp (ms), elapsed_time (ms)
    if (!Number.isFinite(fieldValue)) return "";
    // Timestamps are large epoch ms; elapsed often similar magnitude when both are Date.now().
    // Prefer readable date when value looks like an epoch ms (>= year 2001).
    if (fieldValue >= 978307200000) {
      try {
        return new Date(fieldValue).toLocaleString();
      } catch {
        return String(fieldValue);
      }
    }
    // Small numbers: slider scores or elapsed ms under ~11 days
    if (fieldValue >= 1000 && fieldValue < 978307200000) {
      // Likely elapsed milliseconds — show as seconds with unit hint
      const seconds = fieldValue / 1000;
      if (seconds >= 60) {
        const mins = Math.floor(seconds / 60);
        const secs = Math.round(seconds % 60);
        return `${mins}m ${secs}s`;
      }
      return `${seconds % 1 === 0 ? seconds : seconds.toFixed(1)}s`;
    }
    return String(fieldValue);
  }

  if (typeof fieldValue === "boolean") {
    return fieldValue ? "true" : "false";
  }

  if (typeof fieldValue === "object" && fieldValue !== null) {
    const obj = fieldValue as Record<string, any>;

    // timer runtime state → show the MM:SS (or similar) time string
    if (
      typeof obj.time === "string" &&
      obj.time.trim() &&
      ("v" in obj || "mode" in obj || "running" in obj || "paused" in obj)
    ) {
      return obj.time.trim();
    }

    // smart_table values ({rows, totals, columns}) → readable lines per row
    if (Array.isArray(obj.rows) && Array.isArray(obj.columns)) {
      const cols = obj.columns;
      const lines = obj.rows
        .filter((r: any) =>
          cols.some((c: any) => String(r[c.key] ?? "").trim() !== ""),
        )
        .map((r: any) =>
          cols.map((c: any) => `${c.label}: ${r[c.key] ?? ""}`).join(" | "),
        );
      const totalLine =
        obj.totals && Object.keys(obj.totals).length
          ? "TOTALS — " +
            cols
              .filter((c: any) => obj.totals[c.key] !== undefined)
              .map((c: any) => `${c.label}: ${obj.totals[c.key]}`)
              .join(" | ")
          : null;
      return [...lines, totalLine].filter(Boolean).join("\n");
    }

    // checkbox-style map: { optionKey: true/false }
    if (fieldOptions && fieldOptions.length > 0) {
      const selectedLabels: string[] = [];
      for (const [key, value] of Object.entries(obj)) {
        if (value === true) {
          const option = fieldOptions.find((opt) => opt.key === key);
          if (option?.label) {
            selectedLabels.push(option.label);
          }
        }
      }
      if (selectedLabels.length > 0) {
        return selectedLabels.join(", ");
      }
    }

    return JSON.stringify(fieldValue);
  }

  return String(fieldValue);
}
